import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Save, Bot, Key, Home } from 'lucide-react';
import toast from 'react-hot-toast';

type TradingPair = {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  is_active: boolean;
};

interface UserTradingSettingsProps {
  botId: string;
  onClose: () => void;
  onSave: (settings: any) => void;
}

export default function UserTradingSettings({ botId, onClose, onSave }: UserTradingSettingsProps) {
  const [settings, setSettings] = useState({
    position_size: 100,
    selected_pairs: [] as string[]
  });
  const [tradingPairs, setTradingPairs] = useState<TradingPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<any>(null);

  useEffect(() => {
    Promise.all([
      loadTradingPairs(),
      loadSavedSettings()
    ]).finally(() => setLoading(false));
  }, []);

  const loadSavedSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get current bot status
      const { data: status, error: statusError } = await supabase
        .from('user_bot_status')
        .select('settings, is_trading')
        .eq('user_id', user.id)
        .eq('bot_id', botId)
        .single();

      if (statusError && statusError.code !== 'PGRST116') {
        throw statusError;
      }

      if (status) {
        setCurrentStatus(status);
        if (status.settings) {
          setSettings(status.settings);
        }
      }

      // Load selected pairs from bot_trading_pairs
      const { data: selectedPairs, error: pairsError } = await supabase
        .from('bot_trading_pairs')
        .select('pair_symbol')
        .eq('bot_id', botId);

      if (pairsError) throw pairsError;

      if (selectedPairs) {
        setSettings(prev => ({
          ...prev,
          selected_pairs: selectedPairs.map(p => p.pair_symbol)
        }));
      }
    } catch (error) {
      console.error('Error loading saved settings:', error);
    }
  };

  const loadTradingPairs = async () => {
    try {
      const { data, error } = await supabase
        .from('trading_pairs')
        .select('*')
        .eq('is_active', true)
        .order('symbol');

      if (error) throw error;
      setTradingPairs(data || []);
    } catch (error) {
      console.error('Error loading trading pairs:', error);
      toast.error('Failed to load trading pairs');
    }
  };

  const handleSave = async () => {
    if (settings.selected_pairs.length === 0) {
      toast.error('Please select at least one trading pair');
      return;
    }

    if (settings.position_size <= 0) {
      toast.error('Position size must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update bot status while preserving trading state
      const { error: statusError } = await supabase
        .from('user_bot_status')
        .upsert({
          user_id: user.id,
          bot_id: botId,
          settings: settings,
          is_trading: currentStatus?.is_trading || false, // Preserve current trading state
          is_selected: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,bot_id'
        });

      if (statusError) throw statusError;

      // Update trading pairs
      const { error: pairsError } = await supabase
        .from('bot_trading_pairs')
        .upsert(
          settings.selected_pairs.map(symbol => ({
            bot_id: botId,
            pair_symbol: symbol
          }))
        );

      if (pairsError) throw pairsError;

      toast.success('Settings saved successfully');
      onSave(settings);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Group trading pairs by quote asset
  const groupedPairs = tradingPairs.reduce((acc, pair) => {
    const group = pair.quote_asset === 'USDT' ? 'USDT' : 'USD.P';
    if (!acc[group]) acc[group] = [];
    acc[group].push(pair);
    return acc;
  }, {} as Record<string, TradingPair[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Position Size (USDT)
          </label>
          <input
            type="number"
            value={settings.position_size}
            onChange={(e) => setSettings({ ...settings, position_size: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
            min="0"
            step="10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Select Trading Pairs
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* USDT Pairs */}
            <div className="bg-black/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-cyan-400 mb-3">USDT Pairs</h4>
              <div className="space-y-2">
                {groupedPairs['USDT']?.map((pair) => (
                  <label key={pair.symbol} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.selected_pairs.includes(pair.symbol)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSettings(prev => ({
                            ...prev,
                            selected_pairs: [...prev.selected_pairs, pair.symbol]
                          }));
                        } else {
                          setSettings(prev => ({
                            ...prev,
                            selected_pairs: prev.selected_pairs.filter(p => p !== pair.symbol)
                          }));
                        }
                      }}
                      className="h-4 w-4 text-cyan-500"
                    />
                    <span className="text-white">{pair.base_asset}/USDT</span>
                  </label>
                ))}
              </div>
            </div>

            {/* USD.P Pairs */}
            <div className="bg-black/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-cyan-400 mb-3">USD.P Pairs</h4>
              <div className="space-y-2">
                {groupedPairs['USD.P']?.map((pair) => (
                  <label key={pair.symbol} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.selected_pairs.includes(pair.symbol)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSettings(prev => ({
                            ...prev,
                            selected_pairs: [...prev.selected_pairs, pair.symbol]
                          }));
                        } else {
                          setSettings(prev => ({
                            ...prev,
                            selected_pairs: prev.selected_pairs.filter(p => p !== pair.symbol)
                          }));
                        }
                      }}
                      className="h-4 w-4 text-cyan-500"
                    />
                    <span className="text-white">{pair.base_asset}/USD.P</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
          >
            <Save className="h-5 w-5" />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
